// Centralized API utility for booking flows. No mocks. English only.
import type {
  ClinicConfigResponse,
  ServiceApiResponse,
  ProvidersApiResponse,
  ProviderAvailabilityApiResponse,
  CheckoutSessionRequest,
  CheckoutSessionResponse,
  BookingConfirmationRequest,
  BookingConfirmationResponse,
  Testimonial,
} from '@/types/clinic';

const API_BASE_URL = import.meta.env.DEV
  ? '/api'
  : 'https://core-api.elva.ai/api';

async function jsonGet<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    mode: 'cors',
  });
  if (!res.ok) {
    throw new Error(`HTTP error ${res.status}`);
  }
  return (await res.json()) as T;
}

async function jsonPost<TReq, TRes>(url: string, body: TReq): Promise<TRes> {
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
    mode: 'cors',
  });
  if (!res.ok) {
    let message = `HTTP error ${res.status}`;
    try {
      const data = (await res.json()) as { message?: string };
      if (data?.message) message = data.message;
    } catch (_) {
      // ignore parse error
    }
    throw new Error(message);
  }
  return (await res.json()) as TRes;
}

export const BookingApi = {
  getClinicConfigBySlug(slug: string) {
    return jsonGet<ClinicConfigResponse>(
      `${API_BASE_URL}/booking/${slug}/config/`
    );
  },

  getServices(slug: string) {
    return jsonGet<ServiceApiResponse>(
      `${API_BASE_URL}/booking/${slug}/services/`
    );
  },

  getProviders(slug: string, serviceId: string) {
    return jsonGet<ProvidersApiResponse>(
      `${API_BASE_URL}/booking/${slug}/providers/${serviceId}/`
    );
  },

  getProviderAvailability(
    slug: string,
    providerId: string | number,
    params?: { start_date?: string; end_date?: string }
  ) {
    const query = new URLSearchParams();
    if (params?.start_date) query.set('start_date', params.start_date);
    if (params?.end_date) query.set('end_date', params.end_date);
    const base = `${API_BASE_URL}/booking/${slug}/availability/${providerId}/`;
    const url = query.toString() ? `${base}?${query.toString()}` : base;
    return jsonGet<ProviderAvailabilityApiResponse>(url);
  },

  createCheckoutSession(slug: string, request: CheckoutSessionRequest) {
    return jsonPost<CheckoutSessionRequest, CheckoutSessionResponse>(
      `${API_BASE_URL}/booking/${slug}/checkout-session/`,
      request
    );
  },

  verifyReturningPatient(
    slug: string,
    payload:
      | {
          patient_name: string;
          patient_dob: string;
          patient_phone: string;
          patient_email: string;
          first_name: string;
          last_name: string;
        }
      | { first_name: string; last_name: string; DoB: string }
  ) {
    return jsonPost<typeof payload, any>(
      `${API_BASE_URL}/booking/${slug}/verify-returning/`,
      payload
    );
  },

  confirmBooking(slug: string, payload: BookingConfirmationRequest) {
    return jsonPost<BookingConfirmationRequest, BookingConfirmationResponse>(
      `${API_BASE_URL}/booking/${slug}/confirm/`,
      payload
    );
  },

  getTestimonials(slug: string) {
    return jsonGet<Testimonial[]>(
      `${API_BASE_URL}/booking/${slug}/testimonials/`
    );
  },

  hitCounter(locationId: number | string) {
    return jsonGet<void>(
      `${API_BASE_URL}/counter/hit/?location_id=${encodeURIComponent(
        String(locationId)
      )}`
    );
  },
};
